/**
 * Parameter Admin Override System
 *
 * Allows admins to customize AI model parameters:
 * - Visibility (hidden/disabled)
 * - Input/Output configuration
 * - UI component type override
 * - Default value override
 * - Validation rules
 *
 * Storage: ai_models.parameterOverrides (JSONB)
 *
 * @package @genieartinc/schemas
 */

import type { HandleType, UIComponentType } from './parameter-schema';

// ============================================================================
// VALIDATION TYPES
// ============================================================================

export type ValidationType = 'default' | 'seed' | 'positive-integer' | 'email' | 'url';

// ============================================================================
// PARAMETER GROUPING
// ============================================================================

/**
 * Parameter group classification
 * - 'input': Input handles (left side of node)
 * - 'output': Output params (right side of node + JSON data)
 * - 'settings': Node settings (UI controls in settings panel)
 */
export type ParameterGroup = 'input' | 'output' | 'settings';

// ============================================================================
// ADMIN PARAMETER OVERRIDE
// ============================================================================

export interface AdminParameterOverride {
  // ========================================================================
  // GROUP ASSIGNMENT
  // ========================================================================

  /**
   * Parameter group classification
   * Auto-detected:
   * - isInput=true → 'input'
   * - isOutput=true → 'output'
   * - otherwise → 'settings'
   * @default 'settings'
   */
  group?: ParameterGroup;

  // ========================================================================
  // VISIBILITY
  // ========================================================================

  /**
   * Hide parameter from node settings
   * Only applicable for 'settings' group
   * @default false
   */
  hidden?: boolean;

  /**
   * Show but make readonly (user cannot edit)
   * Only applicable for 'settings' group
   * @default false
   */
  disabled?: boolean;

  // ========================================================================
  // INPUT CONFIGURATION
  // ========================================================================

  /**
   * Parameter accepts input from node handle
   * Auto-assigns group='input'
   * @default false
   */
  isInput?: boolean;

  /**
   * Type of input handle (if isInput=true)
   */
  inputType?: HandleType;

  /**
   * Minimum number of input handles allowed
   * @default 0
   */
  minHandles?: number;

  /**
   * Maximum number of input handles allowed
   * @default 1
   */
  maxHandles?: number;

  // ========================================================================
  // OUTPUT CONFIGURATION
  // ========================================================================

  /**
   * Parameter is an output
   * Auto-assigns group='output'
   * @default false
   */
  isOutput?: boolean;

  /**
   * This is the main/primary output (e.g., generated image/video/audio)
   * Only ONE parameter per model can be main output
   * @default false
   */
  isMainOutput?: boolean;

  /**
   * Output mode:
   * - 'handle': Show as output handle on node
   * - 'json': Save to generated item JSON data
   */
  outputMode?: 'handle' | 'json';

  /**
   * Type of output handle (if outputMode='handle')
   */
  outputType?: HandleType;

  // ========================================================================
  // UI COMPONENT OVERRIDE
  // ========================================================================

  /**
   * Override UI component type (ignores auto-detection)
   */
  uiTypeOverride?: UIComponentType;

  /**
   * Display order within group
   * Lower numbers appear first
   * Used for input handles (prompt=1, negative_prompt=2, etc.)
   * and settings (seed=999 at end)
   */
  order?: number;

  /**
   * Original parameter key name (before suffix was added)
   * Used when output parameter has same name as input parameter
   * Example: seed in input → seed_output in output, originalKey="seed"
   */
  originalKey?: string;

  // ========================================================================
  // VALUE OVERRIDES
  // ========================================================================

  /**
   * Override default value from model schema
   */
  defaultOverride?: unknown;

  /**
   * Override minimum value (for numbers)
   */
  minOverride?: number;

  /**
   * Override maximum value (for numbers)
   */
  maxOverride?: number;

  /**
   * Override enum values (for selects)
   */
  enumOverride?: unknown[];

  // ========================================================================
  // VALIDATION
  // ========================================================================

  /**
   * Special validation type
   * - 'seed': Positive integer (0-2147483647)
   * - 'positive-integer': Integer >= 0
   * - 'email': Valid email
   * - 'url': Valid URL
   * - 'default': Standard type validation
   */
  validationType?: ValidationType;

  // ========================================================================
  // METADATA
  // ========================================================================

  /**
   * Admin notes (why this override exists)
   */
  note?: string;

  /**
   * Last updated timestamp
   */
  updatedAt?: string; // ISO string
}

// ============================================================================
// MODEL PARAMETER OVERRIDES (per model)
// ============================================================================

/**
 * Map of parameter name → override config
 * Stored in ai_models.parameterOverrides
 */
export interface ModelParameterOverrides {
  [parameterName: string]: AdminParameterOverride;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if parameter is hidden by admin override
 */
export function isOverrideHidden(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  return overrides?.[paramName]?.hidden ?? false;
}

/**
 * Check if parameter is disabled (readonly) by admin override
 */
export function isOverrideDisabled(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  return overrides?.[paramName]?.disabled ?? false;
}

/**
 * Check if parameter is input handle by admin override
 */
export function isOverrideInput(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  return overrides?.[paramName]?.isInput ?? false;
}

/**
 * Check if parameter is output by admin override
 */
export function isOverrideOutput(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  return overrides?.[paramName]?.isOutput ?? false;
}

/**
 * Get effective default value (override → model schema)
 */
export function getEffectiveDefault(
  paramName: string,
  modelDefault: unknown,
  overrides?: ModelParameterOverrides
): unknown {
  const override = overrides?.[paramName];
  return override?.defaultOverride !== undefined ? override.defaultOverride : modelDefault;
}

/**
 * Get effective range (override → model schema)
 */
export function getEffectiveRange(
  paramName: string,
  modelRange: { min?: number; max?: number },
  overrides?: ModelParameterOverrides
): { min?: number; max?: number } {
  const override = overrides?.[paramName];

  return {
    min: override?.minOverride !== undefined ? override.minOverride : modelRange.min,
    max: override?.maxOverride !== undefined ? override.maxOverride : modelRange.max,
  };
}

/**
 * Get effective enum values (override → model schema)
 */
export function getEffectiveEnum(
  paramName: string,
  modelEnum: unknown[] | undefined,
  overrides?: ModelParameterOverrides
): unknown[] | undefined {
  const override = overrides?.[paramName];
  return override?.enumOverride !== undefined ? override.enumOverride : modelEnum;
}

/**
 * Get override for parameter (null if none)
 */
export function getParameterOverride(
  paramName: string,
  overrides?: ModelParameterOverrides
): AdminParameterOverride | null {
  return overrides?.[paramName] ?? null;
}

/**
 * Validate seed value (special case)
 */
export function validateSeedValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null; // Null is valid (means random)
  }

  const num = Number(value);

  if (!Number.isInteger(num)) {
    return 'Seed must be an integer';
  }

  if (num < 0) {
    return 'Seed must be non-negative';
  }

  if (num > 2147483647) {
    return 'Seed must be less than 2147483647';
  }

  return null;
}

/**
 * Validate positive integer (special case)
 */
export function validatePositiveInteger(value: unknown): string | null {
  const num = Number(value);

  if (!Number.isInteger(num)) {
    return 'Value must be an integer';
  }

  if (num < 0) {
    return 'Value must be non-negative';
  }

  return null;
}

// ============================================================================
// GROUP DETECTION & MANAGEMENT
// ============================================================================

/**
 * Auto-detect parameter group based on override config
 * Priority: isInput > isOutput > default (settings)
 */
export function detectParameterGroup(override: AdminParameterOverride): ParameterGroup {
  // Explicit group takes precedence
  if (override.group) {
    return override.group;
  }

  // Auto-detect from flags
  if (override.isInput) {
    return 'input';
  }

  if (override.isOutput) {
    return 'output';
  }

  return 'settings';
}

/**
 * Check if parameter belongs to input group
 */
export function isInputGroup(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  const override = overrides?.[paramName];
  if (!override) return false;
  return detectParameterGroup(override) === 'input';
}

/**
 * Check if parameter belongs to output group
 */
export function isOutputGroup(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  const override = overrides?.[paramName];
  if (!override) return false;
  return detectParameterGroup(override) === 'output';
}

/**
 * Check if parameter belongs to settings group
 */
export function isSettingsGroup(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  const override = overrides?.[paramName];
  if (!override) return true; // Default to settings
  return detectParameterGroup(override) === 'settings';
}

/**
 * Check if parameter is marked as main output
 */
export function isMainOutput(
  paramName: string,
  overrides?: ModelParameterOverrides
): boolean {
  return overrides?.[paramName]?.isMainOutput ?? false;
}

/**
 * Get main output parameter name (if any)
 * Returns null if no main output defined
 */
export function getMainOutputParam(overrides?: ModelParameterOverrides): string | null {
  if (!overrides) return null;

  for (const [paramName, override] of Object.entries(overrides)) {
    if (override.isMainOutput) {
      return paramName;
    }
  }

  return null;
}

/**
 * Group parameters by their group type
 * Returns { input: [], output: [], settings: [] }
 */
export function groupParametersByType(
  paramNames: string[],
  overrides?: ModelParameterOverrides
): Record<ParameterGroup, string[]> {
  const groups: Record<ParameterGroup, string[]> = {
    input: [],
    output: [],
    settings: [],
  };

  for (const paramName of paramNames) {
    const override = overrides?.[paramName];
    const group = override ? detectParameterGroup(override) : 'settings';
    groups[group].push(paramName);
  }

  return groups;
}

/**
 * Validate that only one parameter is marked as main output
 * Returns error message if validation fails, null if valid
 */
export function validateMainOutputUniqueness(
  overrides?: ModelParameterOverrides
): string | null {
  if (!overrides) return null;

  const mainOutputs = Object.entries(overrides)
    .filter(([, override]) => override.isMainOutput)
    .map(([paramName]) => paramName);

  if (mainOutputs.length > 1) {
    return `Multiple parameters marked as main output: ${mainOutputs.join(', ')}. Only one is allowed.`;
  }

  return null;
}
